import React from 'react';
import { cn, formatStatus } from '../utils';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md';
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'sm', children, ...props }, ref) => {
    const variants = {
      default: 'bg-gray-100 text-gray-800',
      primary: 'bg-blue-100 text-blue-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      danger: 'bg-red-100 text-red-800',
      info: 'bg-cyan-100 text-cyan-800',
    };

    const sizes = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center font-medium rounded-full',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

// Status-specific badge with automatic styling
export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: string;
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, children, ...props }, ref) => {
    const statusVariants: Record<string, BadgeProps['variant']> = {
      open: 'primary',
      closed: 'default',
      booked: 'success',
      pending: 'warning',
      accepted: 'success',
      rejected: 'danger',
      confirmed: 'primary',
      completed: 'success',
      cancelled: 'danger',
      active: 'success',
      inactive: 'default',
    };

    return (
      <Badge
        ref={ref}
        variant={statusVariants[status] || 'default'}
        {...props}
      >
        {children || formatStatus(status)}
      </Badge>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';
